"use client";

import React from "react";

import Image from "next/image";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectContent,
  SelectTrigger,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { Coins } from "lucide-react";

import { StakingContext } from "../context";

const PROVIDERS = [
  {
    name: "Coinbase",
    img: "/icons/coinbase.svg",
  },
  {
    name: "Binance",
    img: "/icons/binance.svg",
  },
];

const OPTIONS = [
  {
    earnings: "1d",
    price: 0.000002,
    currency: 0.21,
  },
  {
    earnings: "7d",
    price: 0.000015,
    currency: 1.49,
  },
  {
    earnings: "30d",
    price: 0.000066,
    currency: 6.37,
  },
  {
    earnings: "365d",
    price: 0.0008,
    currency: 77.55,
  },
  {
    earnings: "730d",
    price: 0.0016,
    currency: 155.1,
  },
];

export function StakingDetailCard() {
  const { selectedToken } = React.use(StakingContext);

  const [amount, setAmount] = React.useState(0);
  const [provider, setProvider] = React.useState("Coinbase");

  return (
    <Card className="bg-background">
      <CardHeader className="px-5 pt-3.5 pb-2">
        <CardTitle className="text-lg">Stake Details</CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        <div className="mb-8 space-y-6">
          <div>
            <Label className="mb-2">Asset (Token)</Label>
            <div className="border-input flex h-10 w-full items-center gap-2 rounded-lg border bg-transparent px-3 py-1 text-base shadow-sm">
              <Image
                src={selectedToken.img}
                alt={selectedToken.cryptoName}
                className="size-4"
                width={256}
                height={256}
              />
              <div className="text-sm font-medium text-white">
                {selectedToken.cryptoName} &nbsp;
                <span className="text-foreground">
                  ({selectedToken.cryptoSymbol})
                </span>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="provider" className="mb-2">
              Provider
            </Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger
                id="provider"
                className="w-full"
                aria-label="Select provider"
              >
                <SelectValue placeholder="Select a provider" />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map(({ name, img }, key) => (
                  <SelectItem value={name} key={key}>
                    <div className="flex items-center gap-2">
                      <Image
                        src={img}
                        alt={name}
                        className="size-4"
                        width={256}
                        height={256}
                      />
                      <div className="text-sm font-medium text-white">
                        {name} &nbsp;
                        <span className="text-success">
                          ({selectedToken.percentage}%)
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="amount" className="mb-2">
              Staking Amount
            </Label>
            <div className="border-input bg-background flex h-full w-full items-center gap-3 rounded-lg border text-base shadow-sm">
              <Input
                type="number"
                id="amount"
                placeholder="Amount"
                className="relative z-10 basis-1/2 rounded-r-[3px] border-0 shadow-none"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
              <div className="text-sm text-white">
                <span>=</span>
                <span className="px-3">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(
                    (amount * selectedToken.price * selectedToken.percentage) /
                      100,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-b border-dashed py-6">
          <p className="text-base font-medium text-white">Stake Earning</p>
          <div className="mt-6 space-y-6">
            {OPTIONS.map(({ earnings, price, currency }, key) => (
              <div key={key} className="flex items-end justify-between">
                <div className="space-y-1">
                  <p className="text-foreground text-xs font-medium">
                    {earnings}&nbsp;Earnings
                  </p>
                  <p className="text-base font-medium text-white">
                    {price} BTC
                  </p>
                </div>
                <p className="text-foreground text-base font-medium">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(currency)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex-col px-5 pt-0 pb-5">
        <div className="mb-5 flex w-full items-center justify-between gap-4">
          <Label htmlFor="flexible-staking" className="space-y-1">
            <p className="text-base font-medium text-white">Flexible Staking</p>
            <p className="text-foreground text-xs">
              Stop your stake anytime you want.
            </p>
          </Label>
          <Checkbox
            id="flexible-staking"
            aria-label="Flexible staking checkbox"
          />
        </div>
        <Button size="lg" className="flex w-full items-center gap-2">
          <Coins className="size-3" />
          Stake Now
        </Button>
      </CardFooter>
    </Card>
  );
}
